import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DownloadToPstFileComponent } from '@common/components/modals/download-to-pst-file/download-to-pst-file.component';
import { DownloadToPSTFileBaseModalData } from '@common/models';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { formatWithSpaces } from '@common/utils';
import { MbsSize, ModalService, ModalSettings } from 'mbs-ui-kit';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ExportToPstHelper {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly modalService = inject(ModalService);

  private errorRoleText = 'Caught error manually: Forbidden role';

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;
  protected readonly isLimitedAdmin = this.ability.isLimitedAdmin;
  protected readonly isSingleUser = this.ability.isSingleUser;
  protected readonly isDomainUser = this.ability.isDomainUser;

  catchErrorDownloadFile(error: HttpErrorResponse, func: () => Observable<any>): Observable<never | any> {
    // 513 - error means that don't selected checkbox 'Direct PST Download' in My Account -> General Tab
    if (error.status === 513) {
      const settings: Partial<ModalSettings> = {
        size: MbsSize.sm,
        data: {
          taskHasStarted: false,
          countRequired: formatWithSpaces(error?.error?.minItemsCountRequiredPasswordProtection || 9999)
        } as Partial<DownloadToPSTFileBaseModalData>
      };

      if (this.isAdmin() || this.isLimitedAdmin()) {
        settings.data = { ...settings.data, id: this.authDomainId() } as DownloadToPSTFileBaseModalData;

        return this.handleCatchError(settings, func);
      }

      if (this.isSingleUser() || this.isDomainUser()) {
        settings.data = { ...settings.data, id: this.authUserId() } as DownloadToPSTFileBaseModalData;

        return this.handleCatchError(settings, func);
      }

      throw new Error(this.errorRoleText);
    }

    return throwError(() => error);
  }

  private handleCatchError(settings: ModalSettings, func: () => Observable<any>): Observable<any> {
    return from(this.modalService.openCustom(DownloadToPstFileComponent, settings)).pipe(
      switchMap(() => {
        settings.data = { ...settings.data, taskHasStarted: true, func };

        return this.modalService.openCustom(DownloadToPstFileComponent, settings);
      }),
      catchError((err) => throwError(() => err))
    );
  }
}
